import './bunker-gate.css';
import { useEffect, useState } from 'react';
import { TextField, Input, Surface, Skeleton } from '@heroui/react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { applyPlatformLocale } from '@/i18n/platformLocale';
import { api } from '@/lib/api';
import { gateSignIn, oidcLoginHref } from '@/lib/oidcLogin';
import { announceSignInRequired, isPlatformEmbedded, startEmbeddedSignIn } from '@/lib/platformEmbed';
import { frameAccountVerdict, useFrameAccount } from '@/lib/frameAccount';
import { exchangeLoginToken } from '@/lib/exchangeLoginToken';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { getSocket, disconnectSocket } from '@/lib/socket';
import { bindServerKickRecheck, type SessionVerdict } from '@/lib/serverKick';
import { bindSocketEvents } from '@/lib/socketEvents';
import { bindMuteSyncRetry, syncMutesFromServer } from '@/lib/muteSettings';
import { resyncBrowserPushOnce } from '@/lib/webPush';
import { isNativeApp } from '@/lib/isNativeApp';
import { isDesktopShell } from '@/lib/desktopBridge';
import { HeroApp } from '@/heroui/HeroApp';
import { InviteAcceptModal } from '@/components/InviteAcceptModal';


/**
 * HomePage handles 4 paths:
 *   1. URL has ?accessToken=...  -> POST /api/auth/exchange, then strip the param
 *      from the URL and continue as authenticated.
 *   2. No token but cookie present -> GET /api/me succeeds, render shell.
 *   3. No token, no cookie       -> render the "open me from cispracs.ru" screen.
 *   4. Exchange failed           -> render an error screen with a retry hint.
 */
/** Only a 401 means the session is gone; an outage must not send anyone to sign-in. */
async function recheckSession(): Promise<SessionVerdict> {
  try {
    await api.me();
    return 'live';
  } catch (err) {
    return (err as { status?: number }).status === 401 ? 'gone' : 'unknown';
  }
}

export function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useAuthStore((s) => s.setUser);
  const setStatus = useAuthStore((s) => s.setStatus);
  const setError = useAuthStore((s) => s.setError);
  const status = useAuthStore((s) => s.status);

  const accessToken = searchParams.get('accessToken');
  // `/invite/<code>` path. ServerSettingsModal generates these URLs;
  // when a friend lands here we either prompt to log in (UnauthGate
  // captures the path and reloads after auth) or, if already logged
  // in, auto-accept the invite below.
  const inviteMatch = location.pathname.match(/^\/invite\/([A-Za-z0-9_-]+)$/);
  const inviteCode = inviteMatch?.[1] ?? null;

  // Step 1: exchange ?accessToken= for a session cookie (one-shot).
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setStatus('loading');
    // strip token from URL so refresh / share doesn't leak it
    const stripToken = () =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('accessToken');
          return next;
        },
        { replace: true },
      );
    // A token already exchanged (409) also ends here: /api/me then decides
    // from the cookie whether we are signed in.
    exchangeLoginToken(api.exchangeAccessToken, accessToken)
      .then(() => {
        if (cancelled) return;
        stripToken();
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || 'auth_failed');
        navigate('/login-error', { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, navigate, setError, setSearchParams, setStatus]);

  // Step 2: load /api/me (uses cookie). Disabled while we're still exchanging.
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    enabled: !accessToken,
    retry: false,
  });

  // In the platform frame: only the account the platform page is signed in as (lib/frameAccount.ts).
  const frameAccount = useFrameAccount();
  const account = frameAccountVerdict(isPlatformEmbedded(), frameAccount, meQuery.data?.user.platformId);

  useEffect(() => {
    if (meQuery.data && account === 'mismatch') {
      // Another account's Bunker session: no chats, no status; the sign-in screen asks the host
      // to sign the right account in.
      disconnectSocket();
      setUser(null);
    } else if (meQuery.data && account === 'ok') {
      setUser({
        ...meQuery.data.user,
        // server returns Date as ISO string already
      });
      applyPlatformLocale(meQuery.data.user.locale, i18n, window.localStorage);
      useDataStore.getState().setGuilds(meQuery.data.guilds);
      useDataStore.getState().setGuildFolders(meQuery.data.guildFolders ?? []);
      useDataStore.getState().setDmChannels(meQuery.data.dm);
      const socket = getSocket();
      bindSocketEvents(socket);
      // The server closed the socket: re-check the session (sign-in when it is gone).
      bindServerKickRecheck(socket, recheckSession, () => setStatus('unauthenticated'));
      if (!socket.connected) socket.connect();
      // Notification settings: deliver what never reached the server, then
      // adopt the server's view (mutes set or lifted on other devices).
      // Retries also fire on the browser's `online` event.
      bindMuteSyncRetry();
      void syncMutesFromServer();
      // Background push: bind this browser's subscription to this sign-in.
      if (!isNativeApp() && !isDesktopShell()) resyncBrowserPushOnce(meQuery.data.user.id);
    } else if (meQuery.isError) {
      setStatus('unauthenticated');
    }
  }, [meQuery.data, meQuery.isError, account, setUser, setStatus]);

  // Tear down socket when this page unmounts (logout, navigate away).
  useEffect(() => {
    return () => disconnectSocket();
  }, []);

  // `/invite/<code>` no longer auto-accepts. Once the user is logged in we
  // render <InviteAcceptModal> (below) so they see the server, its member
  // count, and an explicit Join / Decline choice. The modal hydrates and
  // selects the guild on join, then we navigate home — this replaced the
  // old silent accept whose "joined but not on the server yet" gap came
  // from relying on a sometimes-late `guild:join` socket event.
  const showInviteModal = !!inviteCode && !!meQuery.data?.user?.id;

  if (status === 'loading' || meQuery.isLoading || (meQuery.data && account === 'pending')) {
    // Shell skeleton — paints the chat layout silhouette while /api/me
    // resolves. Discord pre-renders its rail+drawer+chat outline; without
    // this, first-frame just shows centred text and the layout *jumps in*
    // half a second later, which reads as "freshly loaded webpage" rather
    // than "instant native app".
    return <ShellSkeleton />;
  }

  if (status === 'unauthenticated' || meQuery.isError || account === 'mismatch') {
    return <UnauthGate />;
  }

  // HeroUI is the only shell now (the Aether MainLayout has been retired).
  return (
    <>
      <HeroApp />
      {showInviteModal && inviteCode && (
        <InviteAcceptModal
          code={inviteCode}
          onDone={() => navigate('/', { replace: true })}
        />
      )}
    </>
  );
}

/**
 * Loading silhouette of MainLayout. Renders the three columns (rail / drawer
 * / chat) with shimmering placeholders so the first paint matches the shape
 * users will see in two seconds. Uses tokenised colours so it reads correctly
 * in both light + dark themes; the shimmer keyframe is the existing
 * `animate-pulse` utility so we don't add a fresh CSS file.
 */
function ShellSkeleton() {
  const { t } = useTranslation();
  // Build a few placeholder rows. The exact counts mirror common /api/me
  // responses; they're decorative so don't sweat the precision.
  return (
    <div className="flex h-full w-full flex-col bg-bg" aria-busy aria-label={t('common.loading')}>

      {/* TopBar silhouette */}
      <div className="relative z-10 flex h-16 shrink-0 items-center gap-3 px-4">
        {/* Server tiles are squircles now; a round silhouette promised one
            shape and then the real rail painted another. */}
        <Skeleton className="h-7 w-7 rounded-[9px]" />
        <Skeleton className="h-7 w-7 rounded-[9px]" />
        <Skeleton className="h-7 w-7 rounded-[9px]" />
        <Skeleton className="ml-auto h-4 w-24 rounded" />
      </div>
      <div className="relative z-10 flex min-h-0 flex-1">
        {/* Drawer silhouette */}
        <div className="hidden w-64 shrink-0 flex-col gap-2 p-3 md:flex">
          <Skeleton className="mb-3 h-6 w-32 rounded" />
          <div className="font-medium mt-1 text-[10px] text-ink-muted">{t('channels.textGroup')}</div>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-7 rounded-lg" style={{ width: `${80 - i * 6}%` }} />
          ))}
          <div className="font-medium mt-4 text-[10px] text-ink-muted">{t('channels.voiceGroup')}</div>
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-7 rounded-lg" style={{ width: `${70 - i * 8}%` }} />
          ))}
          <Skeleton className="mt-auto h-10 rounded-lg" />
        </div>
        {/* Chat column silhouette */}
        <main className="flex min-w-0 flex-1 flex-col">
          <div className="h-12 shrink-0 px-4 py-3">
            <Skeleton className="h-5 w-40 rounded" />
          </div>
          <div className="flex flex-1 flex-col justify-end gap-3 p-4">
            {[60, 80, 40, 70, 55, 90, 45].map((w, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-3 w-24 rounded" />
                  <Skeleton className="h-4 rounded" style={{ width: `${w}%` }} />
                </div>
              </div>
            ))}
            <Skeleton className="mt-2 h-12 rounded-xl" />
          </div>
        </main>
      </div>
    </div>
  );
}

/** Compact service entry; the existing owner-managed sign-in flow stays unchanged. */
function UnauthGate() {
  // Aether Node Network gate. Dot-matrix particle field replaces the
  // aurora drift. Onest keeps the heading aligned with the app's type.
  // Glass card holds the value-prop copy with a hairline
  // gradient edge.
  //
  // While the cispracs.ru platform integration is still being wired we
  // expose a server-gated "Тестовый вход" form — POST /api/auth/test-login
  // creates a throwaway user and issues a session. The button is shown
  // only when the server's `/api/auth/config` returns `testLoginEnabled:
  // true`, which requires `ENABLE_TEST_LOGIN=true` in /etc/bunker.env.
  // Once cispracs.ru goes live we flip the env back to false → server
  // returns `false` → the button disappears in the next reload.
  const authConfigQuery = useQuery({
    queryKey: ['authConfig'],
    queryFn: () => api.authConfig(),
    retry: false,
    staleTime: 60_000,
  });
  const testLoginEnabled = authConfigQuery.data?.testLoginEnabled === true;
  const { t } = useTranslation();
  // Inside the Electron tabbed shell (1.0.20+) cispracs lives in a sibling tab,
  // so "open cispracs" should switch tabs — NOT navigate this view to cispracs
  // (which frame-busts). Older shells / browsers keep the plain link.
  const desktopShell =
    typeof window !== 'undefined' && window.bunkerDesktop?.tabbedShell === true && !!window.bunkerDesktop.selectTab;
  // With Cispracs ID on, the browser signs in on this origin and comes back
  // to the same path, so an /invite/<code> link survives the sign-in. Inside
  // the Desktop shell too: every tab shares the platform cookie, so this is the
  // fallback when the shell's own automatic sign-in did not finish.
  const signIn = gateSignIn(authConfigQuery.data?.oidcLogin === true, desktopShell);
  // Inside the platform's Chat frame (voice#347) the identity pages are never
  // framed: the sign-in runs in a popup that comes back to POPUP_DONE_PATH,
  // and a link to the platform opens in the whole window, not in the frame.
  const embedded = isPlatformEmbedded();
  // The shell, already signed in, can then pass the whole page through
  // Bunker's sign-in and back instead of waiting for this button.
  useEffect(() => {
    if (embedded && signIn === 'oidc') announceSignInRequired();
  }, [embedded, signIn]);

  return (
    <div className="bunker-entry relative grid min-h-full place-items-center overflow-hidden px-5 py-10 text-center sm:p-8">
      <div className="bunker-entry-card relative z-10 flex w-full flex-col items-center gap-5 sm:gap-6">
        <div className="flex flex-col items-center">
          <h1>
            <img src="/bunker-wordmark.png" width={768} height={254} alt="BUNKER" className="h-auto w-[240px] max-w-full" />
          </h1>
          <p className="mt-3 max-w-xs text-xs leading-relaxed text-ink-dim sm:mt-4 sm:max-w-none sm:text-sm">
            {t('landing.tagline')}
          </p>
        </div>


        {signIn === 'oidc' ? (
          <a
            href={oidcLoginHref(window.location, i18n.resolvedLanguage)}
            target={embedded ? '_blank' : undefined}
            onClick={
              embedded
                ? (e) => {
                    const lang = i18n.resolvedLanguage;
                    const { href, opened } = startEmbeddedSignIn((path) => {
                      const at = path.indexOf('?');
                      const pathname = at < 0 ? path : path.slice(0, at);
                      return oidcLoginHref({ pathname, search: at < 0 ? '' : path.slice(at) }, lang);
                    });
                    // Blocked popup: the plain link opens the same sign-in in a new tab.
                    if (opened) e.preventDefault();
                    else e.currentTarget.href = href;
                  }
                : undefined
            }
            className="bunker-gate-action"
          >
            {t('landing.signInCispracsId')}
          </a>
        ) : signIn === 'desktopTab' ? (
          <button
            type="button"
            onClick={() => window.bunkerDesktop?.selectTab?.('cispracs')}
            className="bunker-gate-action"
          >
            {t('landing.openPlatformDesktop')}
          </button>
        ) : (
          <a
            href="https://cispracs.ru"
            target={embedded ? '_top' : undefined}
            className="bunker-gate-action"
          >
            {t('landing.openPlatform')}
          </a>
        )}

        {testLoginEnabled && <TestLoginForm />}
      </div>
    </div>
  );
}

/**
 * Pre-platform test-login form. Posts `{ displayName }` to
 * `/api/auth/test-login`, gets a session cookie back, and reloads so
 * the parent `meQuery` re-runs and flips the page from UnauthGate into
 * the real MainLayout. Lives below the main CTA so the cispracs.ru
 * path stays the obvious primary choice.
 */
function TestLoginForm() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const trimmed = name.trim();
  const isValid = trimmed.length >= 2 && trimmed.length <= 32;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.testLogin(trimmed);
      // Full reload at the *current* URL so any `/invite/<code>` path
      // the friend landed on is preserved — the parent HomePage will
      // re-run /api/me with the new cookie, and the invite-accept
      // useEffect will auto-join the guild.
      window.location.reload();
    } catch (e2) {
      setErr(
        e2 instanceof Error && e2.message
          ? e2.message
          : i18n.t('auth.signInFailedRetry'),
      );
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="w-full" aria-label={t('auth.testLoginForm')}>
      <Surface variant="default" className="rounded-xl flex flex-col gap-3 px-6 py-5 text-left">
        <div className="font-medium text-[10px] text-accent">
          {t('auth.testLoginTitle')}
        </div>
        <TextField aria-label={t('auth.yourNick')} value={name} onChange={setName} isDisabled={busy}>
          <Input variant="secondary" maxLength={32} autoFocus placeholder={t('auth.yourNick')} />
        </TextField>
        <button
          type="submit"
          disabled={!isValid || busy}
          className="font-medium inline-flex items-center justify-center gap-2 rounded-[8px] bg-white/[0.06] px-4 py-2.5 text-ink transition-[background-color,border-color,color,box-shadow,opacity] hover:bg-white/[0.1] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? t('auth.testLoginBusy') : t('auth.testLoginSubmit')}
        </button>
        {err && <div className="text-xs text-danger-ink">{err}</div>}
      </Surface>
    </form>
  );
}
